import { ColorDynamic } from '@superdispatch/ui';
import { useEffect, useRef } from 'react';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import { useLoadboardAccessContext } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessContext';
import styled from 'styled-components';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { PostingDetailsModalError } from '../../core/postings/PostingDetailsModalError';
import {
  useCurrentPosting,
  usePostingsPageContext,
} from '../../core/postings/PostingsPageContext';
import { getPostingState } from '../../data/dto/PostingDTO';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { usePostingAPI } from '../../data/LoadboardPostingAPI';
import { PostingDetailsUnpostedLoad } from './PostinDetailsUnpostedLoad';
import { PostingDetails } from './PostingDetails';
import { PostingDetailsModal } from './PostingDetailsModal';
import { useLoadboardAccess } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessAPI';

const Styled1 = styled.div`
  background-color: ${ColorDynamic.Teal50};
  color: ${ColorDynamic.Teal500};
  padding: 16px;
  text-align: center;
`;

export function PostingDetailsPage() {
  const {
    router: { closePostingScreen },
  } = useLoadboardContext();
  const { data: access } = useLoadboardAccess();
  const { params } = usePostingsPageContext();
  const {
    openAccessDialog,
    shouldOpenStateSelector,
    openStateSelectionDialog,
  } = useLoadboardAccessContext();
  const { trackLoad } = usePostingAPI();

  const { data, error } = useCurrentPosting();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data?.load) {
      void trackLoad(data.load.guid, data.load.posting_guid);
      trackLoadboardEvent({
        params,
        posting: data,
        name: 'SLB: Opened Load Details',
      });
    }

    if (access?.reported) {
      closePostingScreen();
      return;
    }

    //if shipper is null, it means that the shipper hid their details from unverified carriers,
    //and we need to show verification banners for carrier
    if (data?.load.shipper === null) {
      closePostingScreen();
      openAccessDialog();
    }

    if (data && shouldOpenStateSelector(data)) {
      closePostingScreen();
      openStateSelectionDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.load.guid]);

  if (error) {
    return (
      <PostingDetailsModal>
        <PostingDetailsModalError error={error} />
      </PostingDetailsModal>
    );
  }

  if (!data) {
    return (
      <PostingDetailsModal>
        <ContentProgressIndicator minHeight={240} progressSize={48} />
      </PostingDetailsModal>
    );
  }

  if (getPostingState(data.load, data.carrier_load) === 'unposted') {
    return (
      <PostingDetailsModal>
        <PostingDetailsUnpostedLoad load={data.load} />
      </PostingDetailsModal>
    );
  }

  return (
    <PostingDetailsModal contentKey={data.load.guid} contentRef={contentRef}>
      {data.carrier_load?.status === 'OFFER_ACCEPTED' && (
        <Styled1>Open the Loads tab to view related order</Styled1>
      )}

      <PostingDetails
        posting={data}
        onScrollToFormClick={() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
          }
        }}
      />
    </PostingDetailsModal>
  );
}
